import { and, asc, eq, ilike, inArray, sql } from "drizzle-orm";
import type { DbClient } from "@/server/platform/db/client";
import {
  productCategories,
  productCategoryLinks,
  products,
  productVariants,
} from "@/server/platform/db/schema/products";
import { nowDate } from "@/shared/lib/date-time";
import type { DbTransaction } from "@/shared/types";
import { isAdHocKitSystemProductId } from "@/modules/orders/domain/ad-hoc-kit";
import { listDraftReservedInventoryByPool } from "@/modules/orders/domain/repo/draft-reservations";
import { listAllIncomingQuantityByVariant } from "@/modules/inventory/domain/repo/import-orders-phase13";
import { isBundleVariant } from "../bundle";
import {
  summarizeProductStockAlerts,
  type VariantForStockAlert,
} from "../low-stock";
import {
  computeSaleableStock,
  enrichVariantStockFields,
} from "../shared-stock";
import type { ProductListItemDTO } from "../types";
import { listBundleLinesForVariantIds } from "./bundle-lines";
import { loadCategoriesByProductIds } from "./category-links";

export async function listCategories(client: DbTransaction | DbClient) {
  return client
    .select()
    .from(productCategories)
    .orderBy(asc(productCategories.sortOrder), asc(productCategories.name));
}

export async function createCategory(
  client: DbTransaction | DbClient,
  input: {
    name: string;
    description?: string | null;
    iconKey?: string | null;
    sortOrder?: number;
  },
) {
  const now = nowDate();
  const [row] = await client
    .insert(productCategories)
    .values({
      name: input.name,
      description: input.description ?? null,
      iconKey: input.iconKey ?? null,
      sortOrder: input.sortOrder ?? 0,
      createdAt: now,
    })
    .returning();
  return row;
}

export async function updateCategory(
  client: DbTransaction | DbClient,
  id: string,
  input: {
    name?: string;
    description?: string | null;
    iconKey?: string | null;
    sortOrder?: number;
  },
) {
  const [row] = await client
    .update(productCategories)
    .set({
      ...(input.name !== undefined ? { name: input.name } : {}),
      ...(input.description !== undefined
        ? { description: input.description }
        : {}),
      ...(input.iconKey !== undefined ? { iconKey: input.iconKey } : {}),
      ...(input.sortOrder !== undefined ? { sortOrder: input.sortOrder } : {}),
    })
    .where(eq(productCategories.id, id))
    .returning();
  return row ?? null;
}

export async function listProducts(
  client: DbTransaction | DbClient,
  query: {
    categoryId?: string;
    search?: string;
    lowStock?: boolean;
    includeInactive?: boolean;
  },
): Promise<ProductListItemDTO[]> {
  const conditions = [];
  if (query.categoryId) {
    conditions.push(
      sql`exists (
        select 1 from ${productCategoryLinks}
        where ${productCategoryLinks.productId} = ${products.id}
          and ${productCategoryLinks.categoryId} = ${query.categoryId}
      )`,
    );
  }
  if (!query.includeInactive) {
    conditions.push(eq(products.isActive, true));
  }
  if (query.search?.trim()) {
    conditions.push(ilike(products.name, `%${query.search.trim()}%`));
  }

  const rows = await client
    .select({
      id: products.id,
      name: products.name,
      description: products.description,
      imageKey: products.imageKey,
      isActive: products.isActive,
      lowStockThreshold: products.lowStockThreshold,
      createdAt: products.createdAt,
      updatedAt: products.updatedAt,
    })
    .from(products)
    .where(conditions.length ? and(...conditions) : undefined)
    .orderBy(asc(products.name));

  const visibleRows = rows.filter((r) => !isAdHocKitSystemProductId(r.id));
  const productIds = visibleRows.map((r) => r.id);
  if (productIds.length === 0) return [];

  const categoriesByProduct = await loadCategoriesByProductIds(
    client,
    productIds,
  );

  const variantStats = await client
    .select({
      productId: productVariants.productId,
      variantCount: sql<number>`count(*)::int`,
      totalStock: sql<number>`coalesce(sum(case when ${productVariants.inventoryVariantId} is null then ${productVariants.stockQuantity} else 0 end), 0)::int`,
      minPrice: sql<string>`min(${productVariants.price})`,
      maxPrice: sql<string>`max(${productVariants.price})`,
    })
    .from(productVariants)
    .where(
      and(
        inArray(productVariants.productId, productIds),
        eq(productVariants.isActive, true),
      ),
    )
    .groupBy(productVariants.productId);

  const statsMap = new Map(variantStats.map((s) => [s.productId, s]));

  const allVariants = await client
    .select({
      id: productVariants.id,
      productId: productVariants.productId,
      stockQuantity: productVariants.stockQuantity,
      inventoryVariantId: productVariants.inventoryVariantId,
      unitsPerSale: productVariants.unitsPerSale,
      isInventoryOnly: productVariants.isInventoryOnly,
      isActive: productVariants.isActive,
      lowStockThreshold: productVariants.lowStockThreshold,
      variantType: productVariants.variantType,
    })
    .from(productVariants)
    .where(
      and(
        inArray(productVariants.productId, productIds),
        eq(productVariants.isActive, true),
      ),
    );

  const variantsByProduct = new Map<string, VariantForStockAlert[]>();
  for (const v of allVariants) {
    const list = variantsByProduct.get(v.productId) ?? [];
    list.push(v);
    variantsByProduct.set(v.productId, list);
  }

  const incomingByVariantId = await listAllIncomingQuantityByVariant(client);

  const result: ProductListItemDTO[] = visibleRows.map((row) => {
    const stats = statsMap.get(row.id);
    const totalStock = stats?.totalStock ?? 0;
    const productVariantsForAlert = variantsByProduct.get(row.id) ?? [];
    const { isLowStock, lowStockVariantCount } = summarizeProductStockAlerts(
      productVariantsForAlert,
      incomingByVariantId,
    );
    return {
      id: row.id,
      categories: categoriesByProduct.get(row.id) ?? [],
      name: row.name,
      description: row.description,
      imageKey: row.imageKey,
      isActive: row.isActive,
      lowStockThreshold: row.lowStockThreshold,
      lowStockVariantCount,
      variantCount: stats?.variantCount ?? 0,
      totalStock,
      isLowStock,
      minPrice: stats?.minPrice ?? null,
      maxPrice: stats?.maxPrice ?? null,
      createdAt: row.createdAt,
      updatedAt: row.updatedAt,
    };
  });

  if (query.lowStock) {
    return result.filter((p) => p.isLowStock);
  }

  return result;
}

export async function getProductById(
  client: DbTransaction | DbClient,
  id: string,
) {
  const [row] = await client
    .select({
      id: products.id,
      name: products.name,
      slug: products.slug,
      description: products.description,
      imageKey: products.imageKey,
      isActive: products.isActive,
      lowStockThreshold: products.lowStockThreshold,
      createdAt: products.createdAt,
      updatedAt: products.updatedAt,
    })
    .from(products)
    .where(eq(products.id, id));

  if (!row) return null;

  const categoriesByProduct = await loadCategoriesByProductIds(client, [id]);
  const categories = categoriesByProduct.get(id) ?? [];

  const variants = await client
    .select()
    .from(productVariants)
    .where(eq(productVariants.productId, id))
    .orderBy(asc(productVariants.sortOrder), asc(productVariants.name));

  const poolById = new Map(
    variants
      .filter((v) => !v.inventoryVariantId)
      .map((v) => [v.id, { stockQuantity: v.stockQuantity }]),
  );

  const bundleVariantIds = variants
    .filter((v) => isBundleVariant(v.variantType))
    .map((v) => v.id);
  const bundleLines = await listBundleLinesForVariantIds(
    client,
    bundleVariantIds,
  );
  const linesByBundle = new Map<string, typeof bundleLines>();
  for (const line of bundleLines) {
    const list = linesByBundle.get(line.bundleVariantId) ?? [];
    list.push(line);
    linesByBundle.set(line.bundleVariantId, list);
  }

  return {
    ...row,
    categories,
    variants: variants.map((v) => {
      const enriched = enrichVariantStockFields(v, poolById);
      return {
        ...enriched,
        variantType: isBundleVariant(v.variantType) ? "bundle" : "simple",
        bundleLines: isBundleVariant(v.variantType)
          ? (linesByBundle.get(v.id) ?? [])
          : undefined,
      };
    }),
  };
}

export async function getVariantById(
  client: DbTransaction | DbClient,
  variantId: string,
) {
  const [row] = await client
    .select()
    .from(productVariants)
    .where(eq(productVariants.id, variantId));
  return row ?? null;
}

export async function listProductsForPicker(
  client: DbTransaction | DbClient,
  query?: {
    categoryId?: string;
    search?: string;
    purpose?: "order" | "receive";
    excludeOrderId?: string;
  },
) {
  const purpose = query?.purpose ?? "order";
  const items = await listProducts(client, {
    categoryId: query?.categoryId,
    search: query?.search,
    includeInactive: false,
  });

  const productIds = items.map((p) => p.id);
  if (productIds.length === 0) return [];

  const variants = await client
    .select()
    .from(productVariants)
    .where(
      and(
        inArray(productVariants.productId, productIds),
        eq(productVariants.isActive, true),
      ),
    )
    .orderBy(asc(productVariants.sortOrder));

  const poolById = new Map(
    variants
      .filter((v) => !v.inventoryVariantId)
      .map((v) => [v.id, { stockQuantity: v.stockQuantity }]),
  );

  const variantMap = new Map<string, (typeof variants)[number][]>();
  for (const v of variants) {
    const list = variantMap.get(v.productId) ?? [];
    list.push(v);
    variantMap.set(v.productId, list);
  }

  const bundleVariantIds = variants
    .filter((v) => isBundleVariant(v.variantType))
    .map((v) => v.id);
  const bundleLines = await listBundleLinesForVariantIds(
    client,
    bundleVariantIds,
  );
  const lineCountByBundle = new Map<string, number>();
  for (const line of bundleLines) {
    lineCountByBundle.set(
      line.bundleVariantId,
      (lineCountByBundle.get(line.bundleVariantId) ?? 0) + 1,
    );
  }

  const draftReservedByPool =
    purpose === "order"
      ? await listDraftReservedInventoryByPool(client, {
          excludeOrderId: query?.excludeOrderId,
        })
      : null;

  return items
    .filter((p) => !isAdHocKitSystemProductId(p.id))
    .map((p) => {
      const productVariantsList = (variantMap.get(p.id) ?? [])
        .filter((v) => {
          if (purpose === "receive") {
            return !v.inventoryVariantId;
          }
          return !v.isInventoryOnly;
        })
        .map((v) => {
          const enriched = enrichVariantStockFields(v, poolById);
          const poolId = enriched.inventoryVariantId ?? enriched.id;
          const poolStock =
            poolById.get(poolId)?.stockQuantity ?? enriched.stockQuantity;
          const poolReserved = draftReservedByPool?.get(poolId) ?? 0;
          const availablePoolStock = Math.max(0, poolStock - poolReserved);
          const availableQuantity =
            purpose === "order"
              ? enriched.inventoryVariantId
                ? computeSaleableStock(
                    availablePoolStock,
                    Number(enriched.unitsPerSale) || 1,
                  )
                : availablePoolStock
              : undefined;
          const draftReservedQuantity =
            purpose === "order" && availableQuantity != null
              ? Math.max(0, enriched.stockQuantity - availableQuantity)
              : undefined;

          return {
            id: enriched.id,
            name: enriched.name,
            price: enriched.price,
            stockQuantity: enriched.stockQuantity,
            draftReservedQuantity,
            availableQuantity,
            stockUnitLabel: enriched.stockUnitLabel,
            receiveUnitLabel: enriched.receiveUnitLabel,
            receiveConversionFactor: enriched.receiveConversionFactor,
            imageKey: enriched.imageKey,
            inventoryVariantId: enriched.inventoryVariantId,
            unitsPerSale: enriched.unitsPerSale,
            isInventoryOnly: enriched.isInventoryOnly,
            variantType: isBundleVariant(v.variantType) ? "bundle" : "simple",
            bundleLineCount: isBundleVariant(v.variantType)
              ? (lineCountByBundle.get(v.id) ?? 0)
              : undefined,
          };
        });

      if (productVariantsList.length === 0) return null;

      return {
        id: p.id,
        name: p.name,
        imageKey: p.imageKey,
        categories: p.categories,
        variants: productVariantsList,
      };
    })
    .filter((p): p is NonNullable<typeof p> => p !== null);
}

export async function getVariantsByIds(
  client: DbTransaction | DbClient,
  variantIds: string[],
) {
  if (variantIds.length === 0) return [];
  return client
    .select()
    .from(productVariants)
    .where(inArray(productVariants.id, variantIds));
}

export async function listVariantsForPriceEdit(
  client: DbTransaction | DbClient,
  query: {
    categoryId?: string;
    search?: string;
    includeInactive?: boolean;
  },
) {
  const conditions = [
    eq(productVariants.isInventoryOnly, false),
  ];
  if (!query.includeInactive) {
    conditions.push(eq(products.isActive, true));
    conditions.push(eq(productVariants.isActive, true));
  }
  if (query.categoryId) {
    conditions.push(
      sql`exists (
        select 1 from ${productCategoryLinks}
        where ${productCategoryLinks.productId} = ${products.id}
          and ${productCategoryLinks.categoryId} = ${query.categoryId}
      )`,
    );
  }
  const search = query.search?.trim();
  if (search) {
    const pattern = `%${search}%`;
    conditions.push(
      sql`(
        ${products.name} ilike ${pattern}
        or ${productVariants.name} ilike ${pattern}
        or coalesce(${productVariants.sku}, '') ilike ${pattern}
      )`,
    );
  }

  const rows = await client
    .select({
      variantId: productVariants.id,
      variantName: productVariants.name,
      sku: productVariants.sku,
      price: productVariants.price,
      productId: products.id,
      productName: products.name,
      isActive: productVariants.isActive,
    })
    .from(productVariants)
    .innerJoin(products, eq(productVariants.productId, products.id))
    .where(and(...conditions))
    .orderBy(asc(products.name), asc(productVariants.sortOrder), asc(productVariants.name));

  return rows.filter((row) => !isAdHocKitSystemProductId(row.productId));
}

